/**
 * Style Profile Module
 *
 * Provides interfaces and methods for loading manuscript style
 * definitions from the MSS API. Requests go through the RTK Query
 * `serverApi`, which deduplicates and caches responses so that
 * switching between profiles does not trigger redundant network calls.
 */

import { serverApi } from "src/api/server-api";

import type { StyleDefinition, StyleProfileResponse } from "src/types";

// The store is imported lazily to avoid a module-init cycle:
// store → review-slice → word-api → paragraph-service → style_profiles → here.
// Deferring to call time lets every module finish initialising first.
async function getStore() {
  return (await import("src/store")).store;
}

/**
 * Loads style definitions for the given profile name.
 *
 * Dispatches the RTK Query `getFlattenedStyles` endpoint, whose cache
 * serves repeat calls for the same profile without a new request.
 *
 * @param profileName - Name of the style profile to load
 * @returns The flat list of style definitions
 */
export async function loadStyleDefinitions(profileName: string): Promise<StyleDefinition[]> {
  const store = await getStore();
  return store.dispatch(serverApi.endpoints.getFlattenedStyles.initiate(profileName)).unwrap();
}

/**
 * Loads the full profile definition for the given profile name.
 *
 * Dispatches the RTK Query `getStyleProfile` endpoint, whose cache
 * serves repeat calls for the same profile without a new request.
 *
 * @param profileName - Name of the style profile to load
 * @returns The full profile response
 */
export async function loadStyleProfile(profileName: string): Promise<StyleProfileResponse> {
  const store = await getStore();
  return store.dispatch(serverApi.endpoints.getStyleProfile.initiate(profileName)).unwrap();
}

/**
 * Builds a lookup map from style name to the full StyleDefinition.
 * This allows efficient access to all style properties with a single
 * lookup.
 *
 * @param profileName - Name of the style profile to use
 * @returns A Map where keys are style names and values are
 *          StyleDefinition objects
 */
export async function buildStyleDefinitionMap(
  profileName: string
): Promise<Map<string, StyleDefinition>> {
  const definitions = await loadStyleDefinitions(profileName);
  const map = new Map<string, StyleDefinition>();

  for (const styleDef of definitions) {
    map.set(styleDef.style, styleDef);
  }

  return map;
}

/**
 * Represents a profile style that is not available in the document.
 */
export interface UnavailableProfileStyle {
  profileStyleName: string; // Style name from profile (e.g., "Heading 3")
  styleCode: string; // Style code from profile (e.g., "h3")
  styleTag: string; // Normalized tag (e.g., "h3")
}
